import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';
import { getCaretCoordinates } from '../lib/caret';
import {
  suggestionsFor,
  tokenAt,
  type EditorMode,
  type SuggestModel,
  type Suggestion,
} from '../lib/autocomplete';

/**
 * A monospace textarea with path/function/filter autocomplete. The popover is
 * anchored at the token under the caret and driven by the shared engine, so what
 * it offers is exactly what the language can resolve against a real bet.
 */
export function AutocompleteTextarea({
  value,
  onChange,
  mode,
  model,
  rows,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  mode: EditorMode;
  model: SuggestModel;
  rows?: number;
  placeholder?: string;
  className?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Suggestion[]>([]);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Restore the caret after an accept re-renders the textarea with new text.
  useLayoutEffect(() => {
    if (pendingCaret.current !== null && ref.current) {
      const c = pendingCaret.current;
      ref.current.selectionStart = ref.current.selectionEnd = c;
      pendingCaret.current = null;
      ref.current.focus();
    }
  });

  const refresh = (text: string, caret: number) => {
    const ta = ref.current;
    if (!ta || caret !== ta.selectionEnd) {
      setOpen(false);
      return;
    }
    const ctx = tokenAt(text, caret, mode);
    if (ctx.kind === 'none') {
      setOpen(false);
      return;
    }
    const next = suggestionsFor(ctx, mode, model);
    // JSONata only returns a path token once something is typed; Liquid can return
    // an empty-partial path just inside `{{` — both are worth showing.
    if (next.length === 0) {
      setOpen(false);
      return;
    }
    const coords = getCaretCoordinates(ta, ctx.start);
    setPos({ top: coords.top - ta.scrollTop + coords.height + 2, left: coords.left - ta.scrollLeft });
    setItems(next);
    setActive(0);
    setOpen(true);
  };

  const accept = (sug: Suggestion) => {
    const ta = ref.current;
    if (!ta) return;
    const caret = ta.selectionStart;
    const ctx = tokenAt(value, caret, mode);
    const nextText = value.slice(0, ctx.start) + sug.apply + value.slice(ctx.end);
    pendingCaret.current = ctx.start + sug.apply.length;
    onChange(nextText);
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + items.length) % items.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      accept(items[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          refresh(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={onKeyDown}
        onKeyUp={(e) => {
          // caret moves that aren't handled by the popover nav
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
            refresh(value, e.currentTarget.selectionStart);
          }
        }}
        onClick={(e) => refresh(value, e.currentTarget.selectionStart)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        rows={rows}
        placeholder={placeholder}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        className={className}
      />

      {open && (
        <ul
          className="absolute z-50 max-h-[220px] min-w-[220px] max-w-[340px] overflow-auto border border-hairline-strong bg-surface-raised shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          {items.map((s, i) => (
            <li key={`${s.kind}:${s.label}`}>
              <button
                type="button"
                // onMouseDown (not click) so it fires before the textarea blur closes us.
                onMouseDown={(e) => {
                  e.preventDefault();
                  accept(s);
                }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 px-2 py-1 text-left font-mono text-[12px]',
                  i === active ? 'bg-accent/15 text-text' : 'text-text-dim',
                )}
              >
                <span className="flex items-center gap-1.5 truncate">
                  <KindGlyph kind={s.kind} />
                  <span className="truncate">{s.label}</span>
                </span>
                {s.detail && (
                  <span className="shrink-0 font-condensed text-[10px] uppercase tracking-wide text-text-faint">
                    {s.detail}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KindGlyph({ kind }: { kind: Suggestion['kind'] }) {
  const map: Record<Suggestion['kind'], { ch: string; cls: string }> = {
    root: { ch: '◆', cls: 'text-accent' },
    field: { ch: '·', cls: 'text-text-faint' },
    func: { ch: 'ƒ', cls: 'text-info' },
    filter: { ch: '|', cls: 'text-warn' },
    tag: { ch: '%', cls: 'text-warn' },
  };
  const m = map[kind];
  return <span className={cn('w-3 shrink-0 text-center', m.cls)}>{m.ch}</span>;
}
